import { useState, type FC } from 'react';
import { ChevronRight } from 'lucide-react';
import type { ShipmentLineDto } from '@/api';
import { cn } from '@/ui';

import { formatNumber, toNumber } from '../lib/format';

/*
 * Товары отгрузки в строке списка - ProductsCell макета: свёрнуто - число товаров и
 * первый из них, по клику - все позиции с кодом и количеством. Клик по ячейке не
 * открывает карточку: строка списка ведёт туда своим кликом.
 */

const goodsWord = (count: number): string => {
  if (count % 10 === 1 && count % 100 !== 11) return 'товар';
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100))
    return 'товара';
  return 'товаров';
};

export const ShipmentLinesCell: FC<{
  lines: ReadonlyArray<ShipmentLineDto>;
}> = ({ lines }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [first] = lines;

  if (first === undefined)
    return <span className="text-ink-400">нет позиций</span>;

  return (
    <div className="flex min-w-0 flex-col items-start">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="inline-flex max-w-full cursor-pointer items-center gap-1.5 overflow-hidden rounded-full border border-ink-150 bg-ink-50 py-0.5 pr-2 pl-1 text-[11.5px] whitespace-nowrap text-ink-600 hover:border-brand-green-100 hover:bg-brand-green-50 hover:text-brand-green-700"
      >
        <ChevronRight
          aria-hidden
          className={cn(
            'size-[11px] shrink-0 transition-transform',
            isOpen && 'rotate-90',
          )}
        />
        {lines.length} {goodsWord(lines.length)}
        {!isOpen && (
          <span className="min-w-0 truncate text-ink-500">
            · {first.productName}
          </span>
        )}
      </button>

      {isOpen && (
        <ul className="m-0 mt-2 flex list-none flex-col gap-2 border-l-2 border-ink-150 p-0 pl-2.5">
          {lines.map((line) => (
            <li key={line.lineNumber} className="flex min-w-0 flex-col">
              <span className="text-[13px] font-semibold text-ink-900">
                {line.productName}
              </span>
              <span className="mt-0.5 text-[11.5px] text-ink-500 tabular-nums">
                {line.productCode} · {formatNumber(toNumber(line.quantity))}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
